/**
 * Componente de Chat
 * Basado en ChatScreen.cs
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChatMessage } from '../../types';
import { ChatMessage as ChatMessageComponent } from './ChatMessage';
import { useChatFeatures } from '../../hooks/useChatFeatures';
import './Chat.css';

interface ChatProps {
  messages: ChatMessage[];
  onSendMessage?: (text: string) => void;
  inline?: boolean;
  onVisibilityChange?: (isVisible: boolean) => void;
  /**
   * Controlar visibilidad desde fuera (prop controlada)
   */
  isVisibleControlled?: boolean;
  /**
   * Modo actual de práctica (para habilitar/deshabilitar funcionalidades)
   */
  mode?: string;
  /**
   * Habilitar copiar/pegar en el input
   * Por defecto: true
   */
  enableCopyPaste?: boolean;
  /**
   * Habilitar reproducir audio al hacer click en burbujas
   * Por defecto: true
   */
  enableAudioReplay?: boolean;
}

export function Chat({ 
  messages, 
  onSendMessage, 
  inline = false, 
  onVisibilityChange,
  isVisibleControlled,
  mode,
  enableCopyPaste = true,
  enableAudioReplay = true
}: ChatProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [inputText, setInputText] = useState('');
  const isSendingRef = useRef(false); // Guard para evitar envíos duplicados
  
  // Si se proporciona isVisibleControlled, usar ese valor en lugar del estado interno
  const actualIsVisible = isVisibleControlled !== undefined ? isVisibleControlled : isVisible;
  
  // Hook reutilizable para funcionalidades del chat
  const { handlePaste, handleMessageClick, handleCopyMessage } = useChatFeatures({
    enableCopyPaste,
    enableAudioReplay,
    mode
  });

  // Sincronizar estado interno cuando cambia isVisibleControlled
  useEffect(() => {
    if (isVisibleControlled !== undefined) {
      setIsVisible(isVisibleControlled);
    }
  }, [isVisibleControlled]);

  // Notificar cambios de visibilidad
  useEffect(() => {
    onVisibilityChange?.(actualIsVisible);
  }, [actualIsVisible, onVisibilityChange]);

  // Auto-scroll al final cuando hay nuevos mensajes
  useEffect(() => {
    if (scrollRef.current && actualIsVisible) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, actualIsVisible]);

  // Focus en el input cuando se muestra el chat
  useEffect(() => {
    if (actualIsVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [actualIsVisible]);

  const handleToggle = () => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    onVisibilityChange?.(newVisibility);
  };

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Guard para evitar envíos duplicados
    if (isSendingRef.current) {
      return;
    }
    
    const textToSend = inputText.trim();
    
    // Permitir enviar incluso si está vacío - sendTextMessage manejará el caso
    if (onSendMessage) {
      isSendingRef.current = true; // Marcar como enviando
      setInputText(''); // Limpiar input primero
      onSendMessage(textToSend);
      
      // Resetear flag después de un breve delay para permitir el siguiente envío
      setTimeout(() => {
        isSendingRef.current = false;
      }, 500);
    }
  };

  if (inline) {
    // Modo inline: botón en la barra de controles y panel flotante
    return (
      <>
        <button
          className="chat-toggle-button-inline"
          onClick={handleToggle}
          aria-label={actualIsVisible ? 'Ocultar chat' : 'Mostrar chat'}
          title={actualIsVisible ? 'Cerrar chat' : 'Abrir chat'}
        >
          <img 
            src="/assets/chat-ui/Boton_Texto_Color.png" 
            alt="Chat"
            className="chat-icon"
          />
        </button>
        {/* Portal: renderiza el panel fuera de .ui-controls para que position:fixed funcione correctamente */}
        {actualIsVisible && createPortal(
          <div className="chat-panel-inline">
            <div className="chat-messages" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="chat-empty">
                  <p>No hay mensajes aún. Escribe algo para comenzar.</p>
                </div>
              ) : (
                messages.map((message, index) => (
                  <ChatMessageComponent
                    key={index}
                    message={message}
                    onClick={handleMessageClick}
                    onCopy={handleCopyMessage}
                  />
                ))
              )}
            </div>
            
            <form className="chat-input-form" onSubmit={handleSend}>
              <input
                ref={inputRef}
                type="text"
                className="chat-input"
                placeholder="Escribe un mensaje..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onPaste={(e) => handlePaste(e, setInputText)}
                onKeyDown={(e) => {// Ctrl+C o Cmd+C funciona nativamente en inputs, pero lo documentamos
                  if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                    // El navegador maneja esto automáticamente para inputs
                    // Solo log para debugging
                    if (inputRef.current && inputRef.current.selectionStart !== inputRef.current.selectionEnd) {
                    }
                  }
                }}
              />
              <button type="submit" className="chat-send-button" aria-label="Enviar mensaje">
                <img 
                  src="/assets/chat-ui/botonenter.png" 
                  alt="Enviar"
                  className="send-icon"
                />
              </button>
            </form>
          </div>,
          document.body
        )}
      </>
    );
  }

  return (
    <div className={`chat-container ${actualIsVisible ? 'visible' : ''}`}>
      <button
        className="chat-toggle-button"
        onClick={handleToggle}
        aria-label={actualIsVisible ? 'Ocultar chat' : 'Mostrar chat'}
        title={actualIsVisible ? 'Cerrar chat' : 'Abrir chat'}
      >
        <img 
          src="/assets/chat-ui/Boton_Texto_Color.png" 
          alt="Chat"
          className="chat-icon"
        />
      </button>

      {actualIsVisible && (
        <div className="chat-panel">
          <div className="chat-messages" ref={scrollRef}>
            {messages.length === 0 ? (
              <div className="chat-empty">
                <p>No hay mensajes aún. Escribe algo para comenzar.</p>
              </div>
            ) : (
              messages.map((message, index) => (
                <ChatMessageComponent
                  key={index}
                  message={message}
                  onClick={handleMessageClick}
                  onCopy={handleCopyMessage}
                />
              ))
            )}
          </div>
          
          <form className="chat-input-form" onSubmit={handleSend}>
            <input
              ref={inputRef}
              type="text"
              className="chat-input"
              placeholder="Escribe un mensaje..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onPaste={(e) => handlePaste(e, setInputText)}
              onKeyDown={(e) => {
                // Ctrl+C o Cmd+C funciona nativamente en inputs, pero lo documentamos
                if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                  // El navegador maneja esto automáticamente para inputs
                  // Solo log para debugging
                  if (inputRef.current && inputRef.current.selectionStart !== inputRef.current.selectionEnd) {
                  }
                }
              }}
            />
            <button type="submit" className="chat-send-button" aria-label="Enviar mensaje">
              <img 
                src="/assets/chat-ui/botonenter.png" 
                alt="Enviar"
                className="send-icon"
              />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}


